/**
 * Dashboard · Course Progress
 *
 * Compact stack of horizontal progress bars — one per course the
 * viewer has started (any lesson complete). Silent for un-started
 * courses so the widget stays lean. Each row deep-links to the
 * course detail page or directly to the next incomplete lesson.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Play, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useTranslation } from "react-i18next";

export default function CourseProgress() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const [rows, setRows] = useState(null); // null = loading, [] = empty
  useEffect(() => {
    api.get(`/progress/by-course?language=${lang}`)
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  }, [lang]);

  // Loading skeleton is intentionally minimal — one line so the layout
  // shifts by a tiny amount instead of a big flash.
  if (rows === null) return null;
  if (rows.length === 0) return null;

  return (
    <section data-testid="dashboard-course-progress" className="space-y-3">
      <div className="flex items-baseline justify-between">
        <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold">
          — {t("courseProgressUi.heading")}
        </div>
        <Link
          to="/courses"
          className="text-sm text-blue-800 font-semibold hover:underline"
        >
          {t("courseProgressUi.allCourses")} →
        </Link>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {rows.map((r) => (
          <ProgressRow key={r.course_id} row={r} />
        ))}
      </div>
    </section>
  );
}

function ProgressRow({ row }) {
  const { t } = useTranslation();
  const complete = row.percent >= 100;
  return (
    <Link
      to={`/courses/${row.course_id}`}
      data-testid={`dashboard-course-progress-row-${row.course_id.slice(-6)}`}
      className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors group"
    >
      <img
        src={row.cover_image}
        alt=""
        className="w-14 h-14 rounded-lg object-cover shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 justify-between">
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 truncate group-hover:text-blue-800 transition-colors">
              {row.title}
            </div>
            <div className="mt-0.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
              {row.level && <span>{row.level}</span>}
              {row.level && <span className="text-slate-300">·</span>}
              <span className="tabular-nums">
                {t("courseProgressUi.lessonsCount", { completed: row.completed_lessons, total: row.total_lessons })}
              </span>
            </div>
          </div>
          <div
            className={`shrink-0 text-lg font-display font-black tabular-nums ${
              complete ? "text-emerald-600" : "text-slate-900"
            }`}
          >
            {row.percent}%
          </div>
        </div>
        <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              complete
                ? "bg-gradient-to-r from-emerald-500 to-emerald-400"
                : "bg-gradient-to-r from-blue-700 to-blue-500"
            }`}
            style={{ width: `${Math.max(row.percent, 3)}%` }}
          />
        </div>
      </div>
      <div className="hidden sm:block shrink-0">
        {complete ? (
          <div className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 uppercase tracking-widest">
            <CheckCircle2 size={14} />
            {t("courseProgressUi.complete")}
          </div>
        ) : (
          <div className="inline-flex items-center gap-1 text-xs font-semibold text-blue-800 uppercase tracking-widest opacity-80 group-hover:opacity-100">
            <Play size={12} fill="currentColor" />
            {t("courseProgressUi.resume")}
          </div>
        )}
      </div>
    </Link>
  );
}
